import React, { useEffect, useMemo, useState } from 'react';
import { View, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { Feather } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { endOfDay, parse, startOfDay } from 'date-fns';

import { ArrowBackHeader, Text } from '_/space-management/components';
import ConfirmReserveModal from '_/space-management/components/ConfirmReserveModal';
import { useDateContext } from '_/space-management/hooks/DateContext';
import { useEventContext } from '_/space-management/hooks/EventContext';
import { useSlotContext } from '_/space-management/hooks/SlotContext';
import { useSpaceContext } from '_/space-management/hooks/SpaceContext';
import { SlotsModel, SlotScheduleModel } from '_/space-management/services/models/slots.model';
import { DATE_FORMAT, IN_FULL_FORMAT } from '_/space-management/config/date';
import { RecurrenceType } from '_/space-management/services/models/enums/recurrence-type.enum';
import { formatDate } from '_/space-management/helpers/formatDate';
import { isScheduleBooked } from '_/space-management/helpers/isScheduleBooked';
import { colors, spacing } from '_/space-management/constants/theme';
import { SafeAreaView } from 'react-native-safe-area-context';

interface RouteParams {
  slot: SlotsModel;
}

export default function SlotScheduleScreen() {
  const route = useRoute();
  const { t } = useTranslation();
  const { slot } = route.params as RouteParams;

  const { selectedDay, setSelectedEventDate, handleChangeSelectedDay, minDate, maxDate } =
    useDateContext();

  const { getSlotAvailability } = useEventContext();
  const { currentSpace } = useSpaceContext();
  const { setIsSlotModalVisible, isSlotModalVisible } = useSlotContext();
  const [recurrenceType, setRecurrenceType] = useState<RecurrenceType>(RecurrenceType.ONCE);

  const [freshEvents, setFreshEvents] = useState(slot.events ?? []);
  const [schedules, setSchedules] = useState<SlotScheduleModel[]>(slot.schedules ?? []);
  const [isFetchingEvents, setIsFetchingEvents] = useState(false);

  const dateObj = useMemo(() => {
    return selectedDay ? parse(selectedDay, DATE_FORMAT, new Date()) : new Date();
  }, [selectedDay]);

  const formattedDate = useMemo(() => {
    return formatDate(dateObj, IN_FULL_FORMAT);
  }, [dateObj]);

  const dayOfWeekNum = useMemo(() => {
    return dateObj.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  }, [dateObj]);

  const matchingSchedules = useMemo(() => {
    if (!schedules) return [];
    return schedules
      .filter((s) => s.dayOfWeek === dayOfWeekNum)
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [schedules, dayOfWeekNum]);

  useEffect(() => {
    if (!currentSpace?.id || !slot.id) return;
    setIsFetchingEvents(true);
    getSlotAvailability(
      slot.id,
      startOfDay(dateObj),
      endOfDay(dateObj),
      true // includeSchedules
    )
      .then((response) => {
        setFreshEvents(response.events ?? []);
        if (response.schedules && response.schedules.length > 0) {
          setSchedules(response.schedules);
        }
      })
      .catch(() => {})
      .finally(() => setIsFetchingEvents(false));
  }, [selectedDay, currentSpace?.id, slot.id]);

  const isMinDate = useMemo(() => {
    return !!(minDate && selectedDay === formatDate(minDate, DATE_FORMAT));
  }, [selectedDay, minDate]);

  const isMaxDate = useMemo(() => {
    return !!(maxDate && selectedDay === formatDate(maxDate, DATE_FORMAT));
  }, [maxDate, selectedDay]);

  const handleSelectSchedule = (schedule: SlotScheduleModel) => {
    const [startH, startM] = schedule.startTime.split(':').map(Number);
    const [endH, endM] = schedule.endTime.split(':').map(Number);

    const start = new Date(dateObj);
    start.setHours(startH, startM, 0, 0);

    const end = new Date(dateObj);
    end.setHours(endH, endM, 0, 0);

    setSelectedEventDate({ start, end });
    // Open the ConfirmReserveModal directly on this screen
    setIsSlotModalVisible(slot.id);
  };

  const handleCloseModal = () => {
    setIsSlotModalVisible('');
    setRecurrenceType(RecurrenceType.ONCE);
  };

  const renderScheduleItem = ({ item }: { item: SlotScheduleModel }) => {
    const isBooked = isScheduleBooked(item, freshEvents, selectedDay);

    return (
      <TouchableOpacity
        style={[styles.scheduleCard, isBooked && styles.bookedCard]}
        onPress={() => handleSelectSchedule(item)}
        activeOpacity={0.7}
        disabled={isBooked || isFetchingEvents}
      >
        <Feather
          name={isBooked ? 'calendar' : 'clock'}
          size={20}
          color={isBooked ? colors.midGrey : colors.green}
          style={styles.clockIcon}
        />
        <Text
          fontSize={18}
          fontType="medium"
          color={isBooked ? colors.midGrey : colors.black}
          style={isBooked && styles.bookedText}
        >
          {item.startTime} - {item.endTime}
        </Text>
        {!isBooked && (
          <Feather name="chevron-right" size={20} color={colors.midGrey} style={styles.arrowIcon} />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <ArrowBackHeader textColor={colors.black} style={styles.header}>
        {slot.name}
      </ArrowBackHeader>

      <View style={styles.dateSelectorContainer}>
        <TouchableOpacity
          style={[styles.arrowButton, isMinDate && styles.disabledButton]}
          disabled={isMinDate}
          onPress={() => handleChangeSelectedDay('sub')}
        >
          <Feather
            name="chevron-left"
            color={isMinDate ? colors.lightGrey : colors.black}
            size={24}
          />
        </TouchableOpacity>

        <View style={styles.dateTextContainer}>
          <Text fontSize={16} fontType="medium" color={colors.darkGrey} numberOfLines={1}>
            {formattedDate}
          </Text>
        </View>

        <TouchableOpacity
          style={[styles.arrowButton, isMaxDate && styles.disabledButton]}
          disabled={isMaxDate}
          onPress={() => handleChangeSelectedDay('add')}
        >
          <Feather
            name="chevron-right"
            color={isMaxDate ? colors.lightGrey : colors.black}
            size={24}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.content}>
        <Text fontSize={18} fontType="bold" color={colors.black} style={styles.title}>
          {t('spaceManage.slotScheduleScreen.selectTime')}
        </Text>

        <FlatList
          data={matchingSchedules}
          keyExtractor={(item) => item.id}
          renderItem={renderScheduleItem}
          contentContainerStyle={styles.listContent}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Feather
                name="calendar"
                size={64}
                color={colors.lightGrey}
                style={styles.emptyIcon}
              />
              <Text fontSize={16} fontType="medium" color={colors.midGrey} style={styles.emptyText}>
                {t('spaceManage.slotScheduleScreen.noSchedules')}
              </Text>
            </View>
          }
        />
      </View>

      {/* ConfirmReserveModal is rendered directly on this screen */}
      {isSlotModalVisible === slot.id && (
        <ConfirmReserveModal
          slot={slot}
          onBackdropPress={handleCloseModal}
          onBackButtonPress={handleCloseModal}
          onSwipeStart={handleCloseModal}
          recurrenceType={recurrenceType}
          setRecurrenceType={setRecurrenceType}
          pickerDisabled
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    paddingBottom: spacing.sm,
  },
  dateSelectorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    marginVertical: spacing.sm,
  },
  arrowButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: colors.lightGrey,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.clear,
  },
  disabledButton: {
    borderColor: colors.white,
    backgroundColor: colors.transparent,
  },
  dateTextContainer: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: spacing.sm,
  },
  content: {
    flex: 1,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
  },
  title: {
    marginBottom: spacing.md,
  },
  listContent: {
    paddingBottom: spacing.lg,
  },
  scheduleCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.clear,
    padding: spacing.md,
    borderRadius: 12,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: colors.lightGrey,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
  },
  bookedCard: {
    borderColor: colors.lightGrey,
    backgroundColor: colors.lightGrey + '20', // very light gray
    opacity: 0.6,
  },
  bookedText: {
    textDecorationLine: 'line-through',
  },
  clockIcon: {
    marginRight: spacing.sm,
  },
  arrowIcon: {
    marginLeft: 'auto',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.lg,
  },
  emptyIcon: {
    marginBottom: spacing.md,
  },
  emptyText: {
    textAlign: 'center',
  },
});
